import ManagerRepository from 'coreClasses/ManagerRepository';
import {parseURL, removeCountryPrefix} from 'coreUtils/url';

import {PRODUCT_DISTINCT_SIZES} from 'brandConstants';

import {
	Product as ProductType,
	RecommendationTypes,
	Recommendation as ProductRecommendation,
	Image as ImageType,
	ТVariationGroupsColorGroup,
	ISwatchImageGroup,
	BundleKitConfig,
	TPersonalizationPreviewConfiguration,
} from '../../store/data/products/types';
import {IProductMeasurements, IPriceRange, IProductPriceObject, IProductVideo, IPersonalizationStyle, EPersonalizationStyle,
	EVideoProviders
} from './IProduct';
import {IThronAsset, parseThronAssetsIds} from '../thron/thronUtils';
import {IProductImageOptions, IProductTransformationObject} from './IProduct';
import {buildPlpVideos, extractThronAssetVideos, parseVideoString} from './videoUtils';
import ProductModel from './ProductModel';
import {findImageGroupBy} from '../../utils/image-groups-utils';

import ProductImage from './Product/ProductImage';
import ThronProductImage from './Product/ThronProductImage';
import {isValidHexColor} from 'coreUtils/colors';

const FEMALE_VIEW_SUFFIX = '_female';

/**
 * Represents ProductModel
 */
abstract class AbstractProductModel {
	managerRepository: ManagerRepository;
	product: ProductType;
	productId: string;

	/**
	 * @constructs
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 * @param {ProductType} product - product
	 */
	constructor(managerRepository: ManagerRepository, product: ProductType) {
		this.managerRepository = managerRepository;
		this.product = product;
		Object.assign(this, product); //TODO: remove and replace with methods
	}

	/**
	 * Returns product object
	 * @returns {ProductType} - product object
	 */
	getProductObject(): ProductType {
		return this.product;
	}

	/**
	 * Returns an array of images for the product for a selected view type.
	 * @param {String} viewType Image view type
	 * @param {IProductTransformationObject} transformationObject
	 * @returns {Array<ProductImage | ThronProductImage>} Array of images
	 */
	getProductImages(
		viewType: string,
		transformationObject: IProductTransformationObject = null,
	): Array<ProductImage | ThronProductImage> {
		const thronAssets = this.getThronAssets();

		if (thronAssets.length > 0) {
			return thronAssets
				.filter((a) => a.type === 'image')
				.map((image) => {
					return new ThronProductImage(
						{
							contentId: image.id,
							productId: this.getProductId(),
							alt: this.getName(),
						},
						transformationObject,
					);
				});
		}

		const genderViewType = this.isFemale() ? `${viewType}${FEMALE_VIEW_SUFFIX}` : viewType;
		const images = findImageGroupBy(this.product?.imageGroups, {
			viewType: genderViewType,
			selectedVariationAttributes: this.product?.variationValues,
		})?.images.map((i) => new ProductImage(i, transformationObject));

		if (images && images?.length) {
			return images;
		}

		const options = {
			viewType,
			selectedVariationAttributes: this.product?.variationValues,
		};

		const imagesWithoutGender = findImageGroupBy(this.product?.imageGroups, options)?.images.map(
			(i) => new ProductImage(i, transformationObject),
		);

		if (this.isFemale()) {
			return imagesWithoutGender;
		}

		return [];
	}

	/**
	 * Returns an image for the product for a selected view type and position.
	 * @param {IProductImageOptions} productImageOptions
	 * @returns {ProductImage|null} Image object
	 */
	getProductImage({
		transformationObject = null,
		viewType = 'zoom',
		index = 0,
		sizeSpecific = false,
	}: IProductImageOptions): ProductImage | ThronProductImage {
		const images = this.getProductImages(viewType, transformationObject);
		const image: ImageType = this.product?.image;
		const imageData: ImageType = image || {
			alt: '',
			link: '',
			disBaseLink: '',
			title: '',
		};

		if (sizeSpecific) {
			const size = this.getSize();

			if (size) {
				const sizeSpecificImage = images.find((image) => {
					const imageName = (image as any).getImageName();

					return imageName.includes(size);
				});

				if (sizeSpecificImage) {
					return sizeSpecificImage;
				}
			}
		}

		return images?.length && images[index] ? images[index] : new ProductImage(imageData, transformationObject);
	}

	/**
	 * Returns videos to display on the Product Listing Page (PLP).
	 * Merges videos from the c_plpProductVideos attribute (Thron IDs, optional positions)
	 * with videos from Thron assets, avoiding duplicates and resolving position conflicts.
	 * @returns {Array.<IProductVideo>} Array of videos sorted by position
	 */
	getPlpVideos(): IProductVideo[] {
		return buildPlpVideos(this.product?.c_plpProductVideos, this.getThronAssets());
	}

	/**
	 * parses c_productVideos attributes
	 * @returns {Array.<IProductVideo>} Array of videos
	 */
	getVideos(): IProductVideo[] {
		const thronAssets = this.getThronAssets();

		if (thronAssets.length > 0) {
			return extractThronAssetVideos(thronAssets);
		}

		return this.product?.c_productVideos
			? parseVideoString(this.product.c_productVideos, EVideoProviders.THRON)
			: [];
	}

	/**
	 * Returns an array of images and videos for the product, in order.
	 * Order is reconstructed using position from getVideos when videos are present; otherwise images first then videos.
	 * @param {String} viewType Image view type (for getProductImages)
	 * @param {IProductTransformationObject} transformationObject
	 * @returns {Array<ProductImage | ThronProductImage | IProductVideo>} Array of images and videos
	 */
	getAssets(
		viewType: string,
		transformationObject: IProductTransformationObject = null,
	): Array<ProductImage | ThronProductImage | IProductVideo> {
		const images = this.getProductImages(viewType, transformationObject);
		const videos = this.getVideos();

		if (videos.length > 0) {
			const total = images.length + videos.length;
			const result: Array<ProductImage | ThronProductImage | IProductVideo> = [];
			let imageIndex = 0;
			let videoIndex = 0;

			for (let position = 0; position < total; position++) {
				if (videos[videoIndex]?.position === position) {
					result.push(videos[videoIndex++]);
				} else {
					result.push(images[imageIndex++]);
				}
			}

			return result;
		}

		return [...images, ...videos];
	}

	/**
	 * Parses c_thronAssetsIds attribute (set-of-string).
	 * Each entry has format "contentId:type" where type is "image" or "video".
	 * @returns {Array.<IThronAsset>} Array of Thron assets
	 */
	private getThronAssets(): IThronAsset[] {
		return parseThronAssetsIds(this.product?.c_thronAssetsIds);
	}

	/**
	 * Checks if the product is a female product.
	 * @returns {Boolean} Female flag status
	 */
	isFemale(): boolean {
		return !!this.product?.c_femaleImage;
	}

	/**
	 * Returns product id
	 * @returns {String}
	 */
	getProductId(): string {
		return this.product?.id;
	}

	/**
	 * Returns master product object
	 * @returns {Object}
	 */
	getMaster(): object {
		return this.product?.master;
	}

	/**
	 * Returns master product id
	 * @returns {String}
	 */
	getMasterProductId(): string {
		return this.product?.master?.masterId;
	}

	/**
	 * Returns the product EAN
	 * @returns {String}
	 */
	getEan(): string {
		return this.product?.ean;
	}

	/**
	 * Returns the raw slug
	 * @returns {string} slug url
	 */
	getSlugUrl(): string {
		return this.product?.slugUrl;
	}

	/**
	 * Returns product url
	 * @returns {string} product url
	 */
	getProductUrl(): string {
		const slugUrl = this.getSlugUrl();

		if (slugUrl) {
			const {pathname, search} = parseURL(this.product?.slugUrl);
			let url = removeCountryPrefix(search ? `${pathname}${search}` : pathname); // Multi-site approach. Prefix alias should be removed, e.g '/{country}/en'

			return this.managerRepository.getLocalesMgr().updateLinkWithLocale(url);
		}

		return '';
	}

	/**
	 * Checks if the product is variation group
	 * @returns {Boolean} is variation group
	 */
	isVariationGroup(): boolean {
		return this.product?.type?.variationGroup;
	}

	/**
	 * Checks if the product is variant
	 * @returns {Boolean} is variant
	 */
	isVariant(): boolean {
		return this.product?.type?.variant;
	}

	/**
	 * Checks if the product is variant
	 * @returns {Boolean} is variant
	 */
	isMaster(): boolean {
		return this.product?.type?.master;
	}

	/**
	 * Checks if the product is a product set
	 * @returns {Boolean} is product set
	 */
	isProductSetType(): boolean {
		return this.product?.type?.set;
	}

	/**
	 * Returns the IDs of the child products in a product set
	 * @returns {string[]} Array of child product IDs
	 */
	getSetProductIds(): string[] {
		// Support both camelCase (setProducts) and snake_case (set_products) from API
		const setProducts = this.product?.setProducts ?? (this.product as unknown as Record<string, unknown>)?.['set_products'] ?? [];

		return Array.isArray(setProducts) ? setProducts.map((p) => (p as {id?: string})?.id).filter(Boolean) as string[] : [];
	}

	/**
	 * Checks if the product is a single variant.
	 * @param {boolean} distinctSizes - returns only one variation per variation value size
	 * @returns {Boolean} True if the product has only one variant
	 */
	isSingleVariant(distinctSizes = PRODUCT_DISTINCT_SIZES): boolean {
		let variants = this.product?.variants || [];

		if (distinctSizes) {
			variants = variants.reduce((newArray, variant, index) => {
				const variationValue = variant.variationValues['size'];
				const alreadyAdded = newArray.find((v) => v.variationValues['size'] === variationValue);

				if (!variationValue || alreadyAdded) {
					return newArray;
				} else if (variant.orderable) {
					return [...newArray, variant];
				} else {
					const inStockAlternative = variants.find(
						(v, i) => i !== index && v.variationValues['size'] === variationValue && v.orderable,
					);

					return [...newArray, inStockAlternative ? inStockAlternative : variant];
				}
			}, []);
		}

		return variants?.length === 1;
	}

	/**
	 * Returns product recommendations.
	 * @param {RecommendationTypes} type Selected recommendation type
	 * @returns {Array<ProductRecommendation>} Array of product recommendations
	 */
	getRecommendations(type: RecommendationTypes): Array<ProductRecommendation> {
		const recommendedProducts =
			this.product?.recommendations?.filter(
				(productRecommendation) => productRecommendation.recommendationType.value === type,
			) || [];

		return recommendedProducts;
	}

	/**
	 * Returns product recommendations attributes.
	 * @return {boolean} hasRecommendationsAttributes
	 */
	hasRecommendationsAttributes(): boolean {
		return !!this.product?.recommendations?.length;
	}

	/**
	 * Returns a string with the variation attribute value
	 * @param {String} variationAttributeName the name of the variation attribute
	 * @returns {String} variation attribute name
	 */
	getVariationValue(variationAttributeName: string): string {
		return this.product?.variationValues?.hasOwnProperty(variationAttributeName)
			? this.product?.variationValues[variationAttributeName]
			: '';
	}

	/**
	 * Returns the product name
	 * @returns {String} product name
	 */
	getName(): string {
		return this.product?.name;
	}

	/**
	 * Returns the product's custom attribute productTag
	 * @returns {String} product tag
	 */
	getProductTag(): string {
		return this.product?.c_productTag;
	}

	/**
	 * Returns the product brand
	 * @returns {String} product brand
	 */
	getBrand(): string {
		return this.product?.brand;
	}

	/**
	 * Returns the product season
	 * @returns {string} season
	 */
	getSeason(): string {
		return this.product?.c_season;
	}

	/**
	 * Returns Brand Category Id
	 * @returns {String} product brand category id
	 */
	getBrandCategoryId(): string {
		return this.product?.c_brandCategoryId;
	}

	/**
	 * Returns the product detail
	 * @returns {String} product detail
	 */
	getDetail(): string {
		return this.product?.detail;
	}

	/**
	 * Returns the product color
	 * @returns {String} product color
	 */
	getColor(): string {
		return this.product?.c_color;
	}

	/**
	 * Returns the product color description
	 * @returns {String} product color description
	 */
	getColorDescription(): string {
		return this.product?.c_colorDescription;
	}

	/**
	 * Returns the color group of the product
	 * @returns {String} color group
	 */
	getColorGroup(): string {
		return this.product?.c_colorGroup || '';
	}

	/**
	 * Returns the product badges
	 * @returns {String[]} product badges IDs
	 */
	getBadgesIds(): string[] {
		return this.product?.c_badges?.split(',');
	}

	/**
	 * Returns the product image badges
	 * @returns {String[]} product image badges IDs
	 */
	getImageBadgesIds(): string[] {
		return this.product?.c_imageBadge?.split(',') || [];
	}

	/**
	 * Returns the product additional links
	 * @returns {String[]} product additionalLinks IDs
	 */
	getAdditionalLinks(): string[] {
		return this.product?.c_additionalLinks?.split(',');
	}

	/**
	 * Returns the product editorial id
	 * @returns {String} product editorial ID
	 */
	getPdpDesktopEditorialID(): string {
		return this.product?.c_pdpDesktopEditorialID;
	}

	/**
	 * Returns the suit piece product IDs from the product set children.
	 * @returns {string[]} Array of product IDs composing the suit (e.g. blazer, trousers)
	 */
	getSuitPieceIDs(): string[] {
		return this.getSetProductIds();
	}

	/**
	 * Returns the product size
	 * @returns {String} product size
	 */
	getSize(): string {
		return this.product?.c_size;
	}

	/**
	 * Returns the primary category id
	 * @returns {String} primary category id
	 */
	getPrimaryCategoryId(): string {
		return this.product?.primaryCategoryId;
	}

	/**
	 * Returns the primary category id
	 * @returns {String} primary category id
	 */
	getCategoryId(): string {
		return this.product?.c_categoryId;
	}

	/**
	 * Returns the short description of the product
	 * @returns {String} short description
	 */
	getShortDescription(): string {
		return this.product?.shortDescription || this.product?.c_shortDescription || '';
	}

	/**
	 * Returns the noReturnCountries of this product
	 * @returns {Array<String>} - c_noReturnCountries custom attribute
	 */
	getNoReturnCountries(): string[] {
		const result = this?.product?.c_noReturnCountries?.split(',');

		return result || new Array();
	}

	/**
	 * Returns the product preorder message
	 * @returns {String} product preorder message
	 */
	getPreorderMessage(): string {
		return this.product?.c_preorderMessage || '';
	}

	/**
	 * Returns if the product is made to order
	 * @returns {Boolean} product is made to order
	 */
	isMadeToOrder(): boolean {
		return this.product?.c_isMadeToOrder || false;
	}

	/**
	 * Returns the product supplier code
	 * @returns {String} product supplier code
	 */
	getSupplierCode(): string {
		return this.product?.c_extendedMfvc || '';
	}

	/**
	 * Returns the product material description
	 * @returns {String} product material description
	 */
	getMaterialDescription(): string {
		return this.product?.c_materialDescription || '';
	}

	/**
	 * Returns the product fit information
	 * @returns {String} product fit information
	 */
	getFitInformation(): string {
		return this.product?.c_fitInformation || '';
	}

	/**
	 * Returns the product size and fit information
	 * @returns {String} product size and fit information
	 */
	getSizeAndFit(): string {
		return this.product?.c_sizeAndFit || '';
	}

	/**
	 * Returns the product shipping information
	 * @returns {String} product shipping information
	 */
	getShippingInformation(): string {
		return this.product?.c_shippingInformation || '';
	}

	/**
	 * Returns the product returns and refund information
	 * @returns {String} product returns and refund information
	 */
	getReturnsAndRefundInformation(): string {
		return this.product?.c_returnsAndRefundInformation || '';
	}

	/**
	 * Returns the product item measurements data
	 * @returns {IProductMeasurements|null} product item measurements data
	 */
	getItemMeasurements(): IProductMeasurements | null {
		let itemMeasurements: IProductMeasurements = null;

		try {
			itemMeasurements = JSON.parse(this.product?.c_itemMeasurements);
		} catch (e) {
			return itemMeasurements;
		}

		return itemMeasurements;
	}

	/**
	 * Checks if the product is loaded
	 * @returns {Boolean} - true if the product is loaded
	 */
	isLoaded(): boolean {
		return !!this.product?.id && !this.product.type?.placeholder;
	}

	/**
	 * Checks if the product is ready to be displayed.
	 * @returns {Boolean} - true if the product is ready to be displayed
	 */
	isReady(): boolean {
		return this.isLoaded();
	}

	/**
	 * Checks if the product is offline.
	 * @returns {Boolean} - true if the product is offline
	 */
	isOffline(): boolean {
		return this.product?.offline;
	}

	/**
	 * Check if the product is a banned with type A.
	 * Banned A products are not visible and not orderable for the customer.
	 * @returns {Boolean} - true if the product is a banned with type A
	 */
	async isBannedA(): Promise<boolean> {
		return this.managerRepository.getBanningMgr().isBanned(this.product?.c_availableForCustomerGroupA);
	}

	/**
	 * Check if the product is a banned with type B.
	 * Banned B products are visible for the customer but not orderable.
	 * The price for such products should be hidden.
	 * @returns {Boolean} - true if the product is a banned with type B
	 */
	async isBannedB(): Promise<boolean> {
		return this.managerRepository.getBanningMgr().isBanned(this.product?.c_availableForCustomerGroupB);
	}

	/**
	 * Check if the product is a banned with type A or B.
	 * @returns {Boolean} - true if the product is a banned with type A or B
	 */
	async isBanned(): Promise<boolean> {
		const isBannedA = await this.isBannedA();
		const isBannedB = await this.isBannedB();

		return isBannedA || isBannedB;
	}

	/**
	 * Returns size guide id
	 * @returns {String} size guide id
	 */
	getSizeGuideId(): string {
		return this.product?.c_sizeGuide;
	}

	/**
	 * Returns true if the product has the style it with recommendations
	 * @returns {Boolean} True if the product has recommendations type Style with it
	 */
	hasStyleItWith(): boolean {
		return !!this.getRecommendations(RecommendationTypes.STYLE_IT_WITH)?.length;
	}

	/**
	 * Returns true if the product has the shop the look recommendations
	 * @returns {Boolean} True if the product has recommendations type Shop The Look
	 */
	hasShopTheLook(): boolean {
		return !!this.getRecommendations(RecommendationTypes.PDP_SHOP_THE_LOOK)?.length;
	}

	/**
	 * Returns true if the product has the print/family recommendations
	 * @returns {Boolean} True if the product has recommendations type Other (BM type 3)
	 */
	hasOtherRecommendations(): boolean {
		return !!this.getRecommendations(RecommendationTypes.OTHER)?.length;
	}

	/**
	 * Returns true if the product has recommendations with virtual try on enabled
	 * @returns {Boolean} True if the product has recommendations type Virtual Try On
	 */
	hasVirtualTryOnRecommendations(): boolean {
		return !!this.getRecommendations(RecommendationTypes.VIRTUAL_TRY_ON)?.length;
	}

	/**
	 * Returns the variation group id
	 * @param {Object} currentVariationValues - variation values from variant
	 * @returns {String|undefined} - variation group id
	 */
	getVariationGroupId(currentVariationValues?: Object): string | undefined {
		let variationGroupId: string | undefined = '';

		if (this.isVariationGroup()) {
			variationGroupId = this.getProductId();
		} else if (currentVariationValues) {
			// matching variation values with values from variationGroups.
			// Useful when we have 2 or more variation groups.
			const variationGroups = this.product?.variationGroups;
			const variationGroupsLength = variationGroups.length;

			if (variationGroupsLength === 1) {
				variationGroupId = variationGroups[0]?.productId;
			} else {
				const variationValuesKeys = Object.keys(currentVariationValues);

				for (let j = 0; j < variationGroups.length; j++) {
					let variationGroup = variationGroups[j];
					let variationGroupValues = variationGroup?.variationValues;

					for (const key of variationValuesKeys) {
						if (key in variationGroupValues && variationGroupValues[key] === currentVariationValues[key]) {
							variationGroupId = variationGroups[j]?.productId;

							break;
						}
					}

					if (variationGroupId) {
						break;
					}
				}
			}
		}

		if (!variationGroupId.length) {
			// PLEASE READ THIS COMMENT BEFORE CHANGING THIS CODE
			// Salesforce return all variation groups of the master products
			// with previous logic (variationGroupId = this.product?.variationGroups[0]?.productId)
			// we always get the first variation group of the master product
			// but we need to get the variation group of the current variant
			// so we need to find the variation group by variation values
			// this prevent us from getting the wrong variation group for check if the product is online
			const currentVariationValues = this.getVariationValues();
			const variationGroups = this.product?.variationGroups || [];
			const isProductSearchEnhancementEnabled = this.managerRepository
				.getConfigurationsMgr()
				.get('sitePreferences.enableEnhancedSearchProductResult');

			const variationGroup = variationGroups.find((variationGroup) => {
				const variationGroupValues = variationGroup?.variationValues || {};
				const variationGroupValuesKeys = Object.keys(variationGroupValues);

				if (!variationGroupValuesKeys.length) {
					return false;
				}

				for (const key of variationGroupValuesKeys) {
					const currentValue = currentVariationValues[key];
					const groupValue = variationGroupValues[key];
					const valuesAreDifferent = groupValue !== currentValue;

					if (
						(isProductSearchEnhancementEnabled && currentValue && valuesAreDifferent) ||
						(!isProductSearchEnhancementEnabled && valuesAreDifferent)
					) {
						return false;
					}
				}

				return true;
			});
			variationGroupId = variationGroup?.productId || '';
		}

		return variationGroupId;
	}

	/**
	 * Returns all variation group ids for product with multiple variation groups
	 * @returns {Array<string>}
	 */
	getVariationGroupIds(): Array<string> {
		let variationGroupIds = [];

		if (this.hasMultipleVariationGroups()) {
			this.product.variationGroups.forEach((variationGroup) => {
				variationGroupIds.push(variationGroup.productId);
			});
		} else {
			variationGroupIds.push(this.product?.variationGroups?.[0]?.productId);
		}

		return variationGroupIds;
	}

	/**
	 * Returns the first variation group id
	 * @returns {String} - first variation group id
	 */
	getFirstVariationGroupId(): string {
		return this.product?.variationGroups?.[0]?.productId || '';
	}

	/**
	 * Returns the variation group
	 * @returns {ProductModel} - variation group
	 */
	getVariationGroup(): ProductModel | this {
		return this.isVariationGroup()
			? this
			: this.managerRepository.getProductsMgr().getProduct(this.getVariationGroupId()) as ProductModel;
	}

	/**
	 * Checks if the product has multiple variation groups
	 * @returns {boolean}
	 */
	hasMultipleVariationGroups(): boolean {
		return this.product?.variationGroups?.length > 1;
	}

	/**
	 * Returns the product type
	 * @returns {String} product type
	 */
	getProductType(): string {
		return this.product?.c_productType;
	}

	/**
	 * Returns object with variation groups ID as a key and first hexadecBasicColor product attribute as a value
	 * @return {Record<string, string>}
	 */
	getVariationGroupsHexColor(): Record<string, string> {
		return this.product?.c_variationGroupsHexColor || {};
	}

	/**
	 * Returns object with variation groups ID as a key and colorDescription product attribute as a value
	 * @return {Record<string, string>}
	 */
	getVariationGroupsColorDescription(): Record<string, string> {
		return this.product?.c_variationGroupsColorDescription || {};
	}

	/**
	 * Returns the variation groups colorGroup attributes by variation id
	 * @returns {ТVariationGroupsColorGroup} variation groups colorGroups attributes by variation id
	 */
	getVariationGroupsColorGroup(): ТVariationGroupsColorGroup {
		return this.product?.c_variationGroupsColorGroup;
	}

	/**
	 * Returns the swatch image URL indexed by variation-group ID.
	 * @returns {Record<string, ISwatchImageGroup>} variation-group swatch image URLs
	 */
	getSwatchImageGroups(): Record<string, ISwatchImageGroup> {
		const swatchImageGroups = this.product?.c_swatchImageGroups || {};
		const localesMgr = this.managerRepository.getLocalesMgr();
		const normalizedGroups: Record<string, ISwatchImageGroup> = {};

		Object.entries(swatchImageGroups).forEach(([variationGroupId, group]) => {
			normalizedGroups[variationGroupId] = {
				imageUrl: group.imageUrl,
				productUrl: group.productUrl
					? localesMgr.updateLinkWithLocale(group.productUrl)
					: '',
				alt: group.alt,
				title: group.title,
			};
		});

		return normalizedGroups;
	}

	/**
	 * Returns the list of valid hex colors from the hexadecBasicColor product attribute
	 * @return {string[]}
	 */
	getProductHexColors(): string[] {
		const hexColors = this.product?.c_hexadecBasicColor ?? [];
		const hexReg = /^#([0-9a-f]{3}){1,2}$/i;
		const transparencyHexReg = /^#[0-9A-F]{6}[0-9a-f]{0,2}$/i;

		return hexColors.filter((hexColor) => {
			const color = hexColor.trim();

			return hexReg.test(color) || transparencyHexReg.test(color);
		});
	}

	/**
	 * Returns string with hexadecBasicColor product attribute as a value
	 * @return {string}
	 */
	getProductHexColor(): string {
		const hexColors = this.getProductHexColors();

		if (hexColors.length === 0) {
			return '';
		}

		return hexColors[0];
	}

	/**
	 * Check if the product is customizable
	 * @returns {boolean} true if the product is customizable
	 */
	isCustomizable(): boolean {
		return this.product?.c_isCustomizable;
	}

	/**
	 * Returns the data layer dimension 34
	 * @returns {String} custom data layer dimension 34 or empty string
	 */
	getDataLayerDimension34(): string {
		return this.product?.c_dataLayerDimension34 || '';
	}

	/**
	 * Returns true if the product has a 3D Model
	 * @returns {boolean}
	 */
	has3DModel(): boolean {
		return this.product?.c_has3dModel;
	}

	/**
	 * Returns true if the product has a virtual mirror model
	 * @returns {boolean}
	 */
	hasVirtualMirror(): boolean {
		return this.product?.c_virtualMirrorEnabled;
	}

	/**
	 * Returns the product price object
	 * @returns {IProductPriceObject} product price
	 */
	getPrice(): IProductPriceObject {
		return {
			value: this.product?.price,
			currency: this.product?.currency,
			pricebook: 'default',
		};
	}

	/**
	 * Returns the product price currency
	 * @returns {String} product price currency
	 */
	getPriceCurrency(): string {
		return this.product?.currency || '';
	}

	/**
	 * Returns the price ranges for the product
	 * @returns {Array<IPriceRange>} price ranges for the product
	 */
	getPriceRanges(): Array<IPriceRange> {
		return this.product?.priceRanges || [];
	}

	/**
	 * Returns is sale price book applied
	 * @returns {Boolean} is sale price book applied
	 */
	isSalePriceBookApplied(): boolean {
		return this.product?.c_isSalesPriceBookApplied;
	}

	/**
	 * Returns the product long description
	 * @returns {String} product long description
	 */
	getLongDescription(): string {
		return this.product?.longDescription || '';
	}

	/**
	 * Returns the ID of the tabs entry for the current product
	 * @returns {String} product tabs id
	 */
	getTabsId(): string {
		return this.product?.c_productTabs || '';
	}

	/**
	 * Returns the product size guide message ID
	 * @returns {String[]} product size guide message ID
	 */
	getSizeGuideMessageId(): string {
		return this.product?.c_sizeGuideMessage || '';
	}

	/**
	 * Returns the product size guide IDs
	 * @returns {String[]} product size guide IDs
	 */
	getSizeGuideIds(): string[] {
		if (!this.getSizeGuideId()) {
			return [];
		}

		return this.getSizeGuideId().split(',');
	}

	/**
	 * Returns the product banning message ID
	 * @returns {String} product banning message ID
	 */
	getBanningMessageId(): string {
		return this.product?.c_banningMessage || '';
	}

	/**
	 * Returns the product custom slots IDs
	 * @returns {String} product slots IDs
	 */
	getCustomSlotsId(): string {
		return this.product?.c_pdpContentAsset || '';
	}

	/**
	 * Returns the product PDP message Contentful ID
	 * @returns {String} product PDP message ID
	 */
	getPdpMessageID(): string {
		return this.product?.c_pdpMessageID || '';
	}

	/**
	 * Returns the product service cards module ID
	 * @returns {String} product service cards module ID
	 */
	getServiceCardsModuleId(): string {
		return this.product?.c_additionalInfoModuleID || '';
	}

	/**
	 * Check if the Virtual Try On Modal Available
	 * @returns {boolean} true if product has Virtual Try On Modal Available
	 */
	getVirtualTryOnEnabled(): boolean {
		return this.product?.c_virtualTryOnEnabled || false;
	}

	/**
	 * Returns variation values
	 */
	getVariationValues() {
		return this.product?.variationValues || {};
	}

	/**
	 * Returns the ID of the tabs entry for the current product
	 * @returns {String[]} product tabs id
	 */
	getTabIds(): string[] {
		return this.product?.c_productTabs?.split(',') || [];
	}

	/**
	 * Returns the care instructions
	 * @returns {String} care instructions
	 */
	getCareInstructions(): string {
		return this.product?.c_careInstructions;
	}

	/**
	 * Checks if product is new arrival
	 * @returns {boolean}
	 */
	isNewArrival(): boolean {
		return this.product?.c_isNewtest;
	}

	/**
	 * Returns the fit information of model height and size
	 * @returns {String} fit information
	 */
	getSecondFitInformation(): string {
		return this.product?.c_fitInformation2 || '';
	}

	/**
	 * If product eligible for engraving
	 * @returns {boolean} - true if product is eligible for engraving
	 */
	isEngravable(): boolean {
		return this.product?.c_isEngravable || false;
	}

	/**
	 * Returns product engrave configuration
	 * @returns {object | null}
	 */
	getEngravingConfiguration(): any | null {
		return this.product?.c_engraveConfiguration || null;
	}

	/**
	 * Returns the product update timestamp
	 * @returns {Number}
	 */
	getUpdateTimestamp(): number {
		return this.product?.updated;
	}

	/**
	 * If product has beautyTryOn
	 * @returns {boolean}
	 */
	isBeautyTryOnEnabled(): boolean {
		return this.product?.c_beautyVirtualTryOnEnabled;
	}

	/**
	 * If product has shadeFinder
	 * @returns {boolean}
	 */
	isShadeFinderEnabled(): boolean {
		return this.product?.c_shadeFinderEnabled;
	}

	/**
	 * Returns the product sub name
	 * @returns {String} product sub name
	 */
	getSubName(): string {
		return this.product?.c_subName;
	}

	/**
	 * Returns the product family name
	 * @returns {String|undefined} product family name
	 */
	getFamilyName(): string | undefined {
		return this.product?.c_familyName;
	}

	/**
	 * Returns the alternative Notify Me status for the product.
	 * @returns {Boolean} True if the product use alternative Notify Me CTA
	 */
	isAlternativeNotifyMeCTA(): boolean {
		return this.product?.c_useAlternativeNotifyMeCTA;
	}

	/**
	 * Returns the searchable if unavailable status for the product.
	 * @returns {Boolean} True if the product searchable if unavailable
	 */
	isSearchableIfUnavailable(): boolean {
		return this.product?.c_isSearchableIfUnavailable;
	}

	/**
	 * Returns the Contentfull ID to manage tax message inside PDP (personalized block)
	 * @returns {String} Contentful Entry ID
	 */
	getTaxMessageID(): string {
		return this.product?.c_taxMessageID;
	}

	/**
	 * Returns the BundleKit type status for the product.
	 * @returns {Boolean} True if the product has BundleKit type
	 */
	isBundleKitType(): boolean {
		const config = this.getBundleKitConfiguration();

		return !!(config && (config.variable || config.fixed));
	}

	/**
	 * Returns the bundle kit configuration for the product.
	 * @returns {BundleKitConfig | Object} The parsed bundle kit configuration object. If the property is undefined
	 * or parsing fails, an empty object is returned.
	 */
	getBundleKitConfiguration(): Partial<BundleKitConfig> {
		try {
			return JSON.parse(this.product?.c_bundleKitConfiguration);
		} catch (e) {
			return {};
		}
	}

	/**
	 * Retrieves the index of the alternative image for the product.
	 * @returns {number} The index of the alternative image, if available.
	 */
	getAlternativeImageIndex(): number {
		return this.product?.c_alternativeImageIndex;
	}

	/**
	 * Returns category IDs for which the product should display the model_alt image.
	 * @returns {string} Category IDs string
	 */
	getCategoryIdForAlternativeImage(): string {
		return this.product?.c_categoryIdForAlternativeImage || '';
	}

	/**
	 * Returns the product variant attributes
	 * @returns {ProductType['variationAttributes']} product variant attributes
	 */
	getVariationAttributes(): ProductType['variationAttributes'] {
		return this.product?.variationAttributes;
	}

	/**
	 * Returns the WashingIcons IDs to manage the care instructions in PDP
	 * @returns {Array<String>} WashingIcons IDs
	 */
	getWashingIconsIds(): Array<string> {
		return this.product?.c_washingIcons;
	}

	/**
	 * Returns if the Washing Icons must be hidden
	 * @returns {Boolean} Hide Washing Icons
	 */
	getHideWashingIcons(): boolean {
		return this.product?.c_hideWashingIcons;
	}

	/**
	 * Returns the logistics product type
	 * @returns {String} product logistics product type
	 */
	getLogisticsProductType(): string {
		return this.product?.c_logisticsProductType ?? '';
	}

	/**
	 * Returns validated PDP background colors for the product.
	 * Only returns colors that are valid hex codes.
	 *
	 * @returns {Record<string, string>} Object with validated pdpBgPrimary and pdpBgSecondary
	 */
	getValidatedPdpBackgroundColors(): Record<string, string> {
		if (!this.product) {
			return {};
		}

		const colorData = {
			pdpBgPrimary: this.product?.c_pdpBackgroundColorPrimary,
			pdpBgSecondary: this.product?.c_pdpBackgroundColorSecondary,
		};

		return Object.keys(colorData).reduce((validObj, key) => {
			const colorValue = colorData[key];

			if (colorValue && isValidHexColor(colorValue)) {
				validObj[key] = colorValue;
			}

			return validObj;
		}, {} as Record<string, string>);
	}

	/**
	 * Returns if the RIS is enabled
	 * @returns {Boolean} RIS enabled
	 */
	getIsEnableRIS(): boolean {
		return this.product?.c_enableRIS;
	}

	/**
	 * Indicates if the product is personalizable.
	 * @returns {Boolean} - True if the product is personalizable
	 */
	isPersonalizable(): boolean {
		return this.getVariationGroup()?.product?.c_isPersonalizable || false;
	}
	/**
	 * Returns the personalizable variation group configuration for the product
	 * @returns {String} personalizable variation group configuration
	 */
	getVariationGroupPersonalizationPreviewConfiguration(): string {
		return (this.getVariationGroup()?.product?.c_personalizationPreviewConfiguration || '') as string;
	}

	/**
	 * Get the configuration used for the online preview.
	 * @returns {TPersonalizationPreviewConfiguration} - Configuration of the personalization preview
	 */
	getPersonalizationPreviewConfiguration(): TPersonalizationPreviewConfiguration {
		let configuration = {};
		const variationGroupPersonalizationPreviewConfiguration =
			this.getVariationGroupPersonalizationPreviewConfiguration();

		if (this.isPersonalizable() && variationGroupPersonalizationPreviewConfiguration) {
			try {
				configuration = JSON.parse(variationGroupPersonalizationPreviewConfiguration);
			} catch (e) {
				console.error('getPersonalizationPreviewConfiguration error', e);
				configuration = variationGroupPersonalizationPreviewConfiguration || {};
			}
		}

		return configuration;
	}

	/**
	 * Get the color of the personalization.
	 * @returns {'GOLD' | 'SILVER' | null} The personalization color.
	 */
	getPersonalizationColor(): 'GOLD' | 'SILVER' {
		if (!this.isPersonalizable()) {
			return null;
		}

		return this.getVariationGroup()?.product?.c_personalizationColor;
	}

	/**
	 * Get the style of the personalization.
	 * @returns {IPersonalizationStyle} The personalization style.
	 */
	getPersonalizationStyle(): IPersonalizationStyle {
		if (!this.isPersonalizable()) {
			return EPersonalizationStyle.titleCase;
		}

		return this.getVariationGroup()?.product?.c_personalizationStyle || EPersonalizationStyle.titleCase;
	}

	/**
	 * Returns the fitOption custom attribute of the product.
	 * @returns {String} model value
	 */
	getFitOption(): string {
		return this.product?.c_fitOption ?? '';
	}

	/**
	 * Returns the collar custom attribute of the product.
	 * @returns {String} model value
	 */
	getCollarType(): string {
		return this.product?.c_collarOption ?? '';
	}

	/**
	 * Returns the Match it with / Complete the Look configuration (JSON string).
	 * JSON shape: { categories: Array<{ id: string, products?: string[] }> }.
	 * @returns {string} matchItWithConfig or empty string
	 */
	getMatchItWithConfig(): string {
		return this.product?.c_matchItWithConfig ?? '';
	}

	/**
	 * Returns value of the product soldOrder custom attribute
	 * @returns {Number} product sold order
	 */
	getSoldOrder(): number {
		return (this.product as ProductType & {c_soldOrder?: number})?.c_soldOrder;
	}

	/**
	 * Returns value of the product showModelMFVC custom attribute
	 * @default true
	 * @returns {Boolean} product showModelMFVC
	 */
	getShowModelMFVC(): boolean {
		return this.product?.c_showModelMFVC ?? true;
	}
}

export default AbstractProductModel;
